import { useState } from "react";
import { X, ShieldAlert, Plus } from "lucide-react";
import { AssignBadge, StaffBadge, Avatar } from "@/components/atlas/Shared";
import { Checkbox } from "@/components/ui/checkbox";
import { NotifyState, NotifyButton, ConflictIcon } from "@/components/staffing/notify";
import { usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";
import { fmtRange } from "@/lib/format";

const statusOf = (filled, required) => (filled >= required ? "full" : filled === 0 ? "vacant" : "partial");

const AssignedRow = ({ a, onRemove, disabled, picked, onPick, eventId, reload }) => {
  const { t } = useLang();
  const dim = a.status === "declined";
  return (
    <div
      draggable={!disabled && !dim}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ type: "assignment", id: a.id }))}
      data-testid={`assigned-row-${a.id}`}
      className={`group flex items-center gap-2 pl-2 pr-2 py-2 rounded-2xl border ${a.conflicts?.length ? "border-red-400/50" : ""} ${dim ? "opacity-50 border-dashed border-red-200" : "border-white/[0.07] bg-white/[0.03] cursor-grab active:cursor-grabbing hover:border-[#C5A059]/40"}`}
    >
      {onPick && !dim && <span onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`select-assignment-${a.id}`} checked={picked} onCheckedChange={() => onPick(a.id)} /></span>}
      <Avatar name={a.employee_name} size={26} dim={dim} />
      <span className={`text-sm flex-1 truncate ${dim ? "line-through" : ""}`}>{a.employee_name}</span>
      <ConflictIcon a={a} />
      {a.override && <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8a6d2f]" title={t("b.override_tag")}><ShieldAlert className="w-3 h-3" /></span>}
      {!dim && <NotifyState a={a} />}
      <AssignBadge status={a.status} testid={`assigned-status-${a.id}`} />
      {!dim && eventId && <NotifyButton a={a} eventId={eventId} reload={reload} />}
      {!disabled && !dim && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(a); }} data-testid={`remove-assignment-${a.id}`} className="press p-1 text-neutral-300 hover:text-red-600" aria-label="remove">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

const AvailableList = ({ slot, people, onAssign, disabled, full }) => {
  const { t } = useLang();
  return (
    <div className="mt-4 pt-3 border-t border-white/[0.06]" data-testid={`slot-available-${slot.id}`}>
      <div className="eyebrow mb-2">{t("b.availableForSlot")} <span className="mono text-[#D9B872]">{people.length}</span></div>
      {people.length === 0 ? <p className="text-xs text-neutral-500">{t("b.noneForSlot")}</p> : (
        <div className="flex flex-wrap gap-1.5">
          {people.map((c) => (
            <button key={c.id} type="button" disabled={disabled || full} data-testid={`slot-available-${slot.id}-${c.id}`} title={full ? t("err.position_full") : t("b.assign")}
              onClick={(e) => { e.stopPropagation(); onAssign(slot, c); }}
              className="press inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-white/[0.08] text-xs hover:border-[#C5A059]/60 disabled:opacity-50 transition-colors">
              <span className={`w-2 h-2 rounded-full ${c.availability === "available" ? "bg-emerald-500" : "bg-[#C5A059]"}`} aria-label={t(`av.${c.availability}`)} />
              {c.name}{c.availability === "maybe" && <span className="text-neutral-500">({t("av.maybe")})</span>}
              {c.assigned_slot_ids.length > 0 && <span className="text-neutral-500" data-testid={`slot-available-elsewhere-${slot.id}-${c.id}`}>· {t("b.elsewhere")}</span>}
              {!disabled && !full && <Plus className="w-3 h-3 text-[#D9B872]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PositionCard = ({ slot, event, selected, onSelect, onDropItem, onRemove, disabled, available = [], onAssign, picked = [], onPick, reload }) => {
  const { t } = useLang();
  const { label } = usePositions();
  const [hot, setHot] = useState(false);
  const active = slot.assignments.filter((a) => a.status !== "declined");
  const declined = slot.assignments.filter((a) => a.status === "declined");
  const filled = active.length;
  const vacancies = Math.max(slot.required - filled, 0);
  const st = statusOf(filled, slot.required);
  const bar = { full: "bg-emerald-500", partial: "bg-[#C5A059]", vacant: "bg-red-400" }[st];

  return (
    <div
      data-testid={`position-card-${slot.id}`}
      onClick={() => onSelect(slot.id)}
      onDragOver={(e) => { if (!disabled) { e.preventDefault(); setHot(true); } }}
      onDragLeave={() => setHot(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHot(false);
        try { onDropItem(slot, JSON.parse(e.dataTransfer.getData("text/plain"))); } catch { /* ignore */ }
      }}
      className={`relative card-lux cursor-pointer press ${selected ? "border-[#C5A059] ring-1 ring-[#C5A059]" : "hover:border-black/20"} ${hot ? "drop-hot" : ""}`}
    >
      <div className={`absolute left-0 top-5 bottom-5 w-[3px] rounded-full ${bar}`} />
      <div className="p-5 pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] truncate" data-testid={`position-name-${slot.id}`}>{label(slot.position_key)}</h3>
            <div className="mono text-[11px] text-neutral-500 mt-1">{fmtRange(slot.start_time || event.start_time, slot.end_time || event.end_time, t)}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="mono text-2xl leading-none" data-testid={`position-count-${slot.id}`}><span className="text-[#D9B872]">{filled}</span><span className="text-neutral-500">/{slot.required}</span></div>
            <div className="mt-2"><StaffBadge status={st} testid={`position-status-${slot.id}`} /></div>
          </div>
        </div>
        {slot.instructions && <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{slot.instructions}</p>}
        <div className="mt-4 space-y-1.5">
          {active.map((a) => <AssignedRow key={a.id} a={a} onRemove={onRemove} disabled={disabled} picked={picked.includes(a.id)} onPick={onPick} eventId={event.id} reload={reload} />)}
          {Array.from({ length: vacancies }).map((_, i) => (
            <div key={`v${i}`} data-testid={`vacant-slot-${slot.id}-${i}`} className="pl-2 pr-3 py-2 rounded-full border border-dashed border-white/15 text-xs text-neutral-500 flex items-center gap-2.5">
              <span className="w-[26px] h-[26px] rounded-full border border-dashed border-white/25 grid place-items-center"><Plus className="w-3 h-3" /></span>
              <span className="flex-1">{t("c.vacant")}</span>
              {hot && i === 0 && <span className="text-[#8a6d2f]">{t("b.drop")}</span>}
            </div>
          ))}
          {declined.map((a) => <AssignedRow key={a.id} a={a} onRemove={onRemove} disabled />)}
        </div>
        <AvailableList slot={slot} people={available} onAssign={onAssign} disabled={disabled} full={vacancies === 0} />
      </div>
    </div>
  );
};
